import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardActionArea,
  CircularProgress,
  Chip,
  Divider,
  Button,
} from "@mui/material";
import ShoppingBagIcon from "@mui/icons-material/ShoppingBag";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import StorefrontIcon from "@mui/icons-material/Storefront";
import { httpClient } from "../httpClient";
import { Order } from "./types";

const STATUS_LABELS: Record<string, string> = {
  payment_pending: "Bezahlung offen",
  in_progress: "In Bearbeitung",
  completed: "Abgeschlossen",
};

const STATUS_COLORS: Record<string, "warning" | "info" | "success" | "default"> = {
  payment_pending: "warning",
  in_progress: "info",
  completed: "success",
};

const PAYMENT_METHOD_LABELS: Record<string, string> = {
  cash: "Bar",
  paypal: "PayPal",
};

const MY_ORDERS_KEY = "dartmin_my_orders";

interface SavedEntry {
  token: string;
  id: number;
  created_at: string;
  total: number;
}

function formatPrice(price: number): string {
  return `€ ${price.toFixed(2).replace(".", ",")}`;
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleString("de-AT", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export default function MyOrdersPage() {
  const navigate = useNavigate();
  const [orders, setOrders] = useState<(Order | null)[]>([]);
  const [loading, setLoading] = useState(true);

  const saved: SavedEntry[] = (() => {
    try {
      return JSON.parse(localStorage.getItem(MY_ORDERS_KEY) ?? "[]");
    } catch {
      return [];
    }
  })();

  useEffect(() => {
    if (saved.length === 0) {
      setLoading(false);
      return;
    }

    Promise.all(
      saved.map((entry) =>
        httpClient(`/api/shop/orders/by-token/${entry.token}`)
          .then(({ json }) => json as Order)
          .catch(() => null)
      )
    ).then((results) => {
      setOrders(results);
      setLoading(false);
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" mt={6}>
        <CircularProgress />
      </Box>
    );
  }

  const validOrders = orders.filter((o): o is Order => o !== null);

  return (
    <Box sx={{ maxWidth: 680, mx: "auto", p: { xs: 1, sm: 2, md: 3 } }}>
      <Typography variant="h5" fontWeight={700} mb={2} display="flex" alignItems="center" gap={1}>
        <ShoppingBagIcon /> Meine Bestellungen
      </Typography>

      {validOrders.length === 0 ? (
        <Box textAlign="center" py={6}>
          <Typography color="text.secondary" mb={2}>
            Du hast noch keine Bestellungen aufgegeben.
          </Typography>
          <Button
            variant="outlined"
            startIcon={<StorefrontIcon />}
            onClick={() => navigate("/shop")}
          >
            Zum Shop
          </Button>
        </Box>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
          {validOrders.map((order) => (
            <Card key={order.id}>
              <CardActionArea onClick={() => navigate(`/shop/order/${order.token}`)}>
                <CardContent>
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                    <Box>
                      {order.order_number && (
                        <Typography variant="caption" color="text.secondary" fontWeight={600}>
                          Bestellung #{order.order_number}
                        </Typography>
                      )}
                      <Typography variant="body2" color="text.secondary">
                        {formatDate(order.created_at)}
                      </Typography>
                      <Typography fontWeight={600} mt={0.25}>
                        {formatPrice(order.total)}
                        {" · "}
                        {order.items?.length ?? 0} Artikel
                      </Typography>
                      {order.items?.slice(0, 2).map((item, i) => (
                        <Typography key={i} variant="body2" color="text.secondary">
                          {item.shop_item_name} × {item.quantity}
                          {item.size ? ` (${item.size})` : ""}
                        </Typography>
                      ))}
                      {(order.items?.length ?? 0) > 2 && (
                        <Typography variant="body2" color="text.secondary">
                          + {(order.items?.length ?? 0) - 2} weitere…
                        </Typography>
                      )}
                    </Box>
                    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 0.5 }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Chip
                          label={STATUS_LABELS[order.status ?? "payment_pending"] ?? order.status}
                          color={STATUS_COLORS[order.status ?? "payment_pending"] ?? "default"}
                          size="small"
                        />
                        <ChevronRightIcon color="action" />
                      </Box>
                      <Chip
                        label={PAYMENT_METHOD_LABELS[order.payment_method] ?? order.payment_method}
                        size="small"
                        variant="outlined"
                      />
                    </Box>
                  </Box>
                </CardContent>
              </CardActionArea>
            </Card>
          ))}

          <Divider sx={{ my: 1 }} />

          <Button
            variant="outlined"
            startIcon={<StorefrontIcon />}
            onClick={() => navigate("/shop")}
            sx={{ alignSelf: "center" }}
          >
            Neue Bestellung
          </Button>
        </Box>
      )}
    </Box>
  );
}
